import { Bounds } from './map-types';

/**
 * Plane geometry over the ABS suburb GeoJSON (#256): bounds, an interior point per suburb, and the lasso's
 * point-in-polygon test. Pure functions, tested in map-geometry.spec.ts.
 */

/** Sampling steps: fitting a state or a region needn't walk every vertex of every suburb. */
export const REGION_BOUNDS_STEP = 5;
const STATE_BOUNDS_STEP = 25;
const MAINLAND_TEST_STEP = 40;
/** Longitudes that hold the mainland and Tasmania: Norfolk Island and the Coral Sea islands sit outside. */
const MAINLAND_WEST = 96;
const MAINLAND_EAST = 154.5;
/** Half the side, in degrees, of the square a single suburb's point is fitted into (about 3 km). */
const POINT_FIT_DEGREES = 0.03;

/** Ray-casting point-in-polygon over one ring. */
export function pointInRing(x: number, y: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Strip ABS's "(NSW)" / "(Richmond Valley - NSW)" qualifiers for labels. */
export function displayName(name: string): string {
  return name.replace(/\s*\(.*?\)\s*$/, '');
}

/** [west, south, east, north] over features; `step` samples vertices (fitting a state needn't walk every one). */
export function bboxOf(features: any[], step = 1): Bounds | null {
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const f of features) {
    if (!f.geometry) continue;
    const coords =
      f.geometry.type === 'Polygon' ? f.geometry.coordinates : f.geometry.coordinates.flat();
    for (const ring of coords) {
      for (let i = 0; i < ring.length; i += step) {
        const [x, y] = ring[i];
        if (x < w) w = x;
        if (x > e) e = x;
        if (y < s) s = y;
        if (y > n) n = y;
      }
    }
  }
  return Number.isFinite(w) ? [w, s, e, n] : null;
}

/**
 * A point that belongs to the suburb, for the lasso test and labels: the vertex mean of its largest ring when that
 * falls inside (a C-shaped or coastal suburb's mean can be outside its own polygon), else the ring's first vertex.
 * Cached on the feature, out of sight of JSON and deck.gl.
 */
export function anchorOf(f: any): [number, number] {
  if (f.__anchor) return f.__anchor;
  const rings =
    f.geometry.type === 'Polygon' ? f.geometry.coordinates : f.geometry.coordinates.flat();
  let best = rings[0];
  for (const r of rings) if (r.length > best.length) best = r;
  let sx = 0;
  let sy = 0;
  for (const [x, y] of best) {
    sx += x;
    sy += y;
  }
  const mean: [number, number] = [sx / best.length, sy / best.length];
  const anchor = pointInRing(mean[0], mean[1], best) ? mean : (best[0] as [number, number]);
  Object.defineProperty(f, '__anchor', { value: anchor, enumerable: false });
  return anchor;
}

/** Bounds for fitting a whole state: the mainland's, so the outlying islands don't zoom the view out to nothing. */
export function mainlandBounds(features: any[]): Bounds | null {
  if (!features.length) return null;
  const main = features.filter((f) => {
    const b = bboxOf([f], MAINLAND_TEST_STEP);
    return b && b[0] > MAINLAND_WEST && b[2] < MAINLAND_EAST;
  });
  return bboxOf(main.length ? main : features, STATE_BOUNDS_STEP);
}

/** A small square around one point (a suburb from the graph, which has no polygon loaded yet). */
export function boundsAround(lng: number, lat: number): Bounds {
  const d = POINT_FIT_DEGREES;
  return [lng - d, lat - d, lng + d, lat + d];
}

/** The axis-aligned box of a screen-space path, for a cheap reject before the ring test. */
function boxOf(path: number[][]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of path) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * The suburbs whose interior point falls inside a screen-space lasso. Suburbs are projected to pixels, so the test
 * holds at any zoom; `project` returns null for a point off the map.
 */
export function codesInPath(
  path: number[][],
  features: any[],
  project: (lng: number, lat: number) => { x: number; y: number } | null
): string[] {
  if (path.length < 3) return [];
  const { minX, minY, maxX, maxY } = boxOf(path);
  const hits: string[] = [];
  for (const f of features) {
    const [lng, lat] = anchorOf(f);
    const p = project(lng, lat);
    if (!p || p.x < minX || p.x > maxX || p.y < minY || p.y > maxY) continue;
    if (pointInRing(p.x, p.y, path)) hits.push(f.properties.code);
  }
  return hits;
}
